import { SPECIAL_POSITION } from '../../config'
import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { PageComponent, Text, Button } from "../../components"
import { SupportModal } from "../get-qr-admin/components/support-modal"
import { API_BASE_URL, SHOW_CATALOG_RATING } from "../../config"
import vIcon from '../../assets/icons/v-icon.svg'
import { CATALOG_CATEGORIES, CATALOG_DESIGNS, CATEGORY_MATERIALS, NO_NFC_CATEGORIES, CatalogCategory, CatalogColor, CatalogDesign } from "./data"
import { CustomDesignPreview, DesignColorEditor, DEFAULT_DESIGN_PALETTE, DesignPalette, PALETTE_FIELDS } from './custom-design'
import { Catalog3D } from './catalog-3d'
import { CatalogSavings } from './savings'
import './style.css'
import './layout.css'

const getOrderMessage = (design: CatalogDesign, color?: CatalogColor, palette?: DesignPalette) => {
    const material = CATEGORY_MATERIALS[design.category]
    const customColors = palette ? `, цвета: ${PALETTE_FIELDS.map(({key, label}) => `${label.toLowerCase()} — ${palette[key].toUpperCase()}`).join(', ')}` : ''
    return `Здравствуйте, интересует ${material ? `${material} ` : ''}визитка «${design.title}»${customColors || (color ? `, цвет: ${color.label}` : '')}`
}

const formatPrice = (price?: number) => price === undefined ? 'Цена по запросу' : `${price.toLocaleString('ru-RU')} ₽`

// шаг между появлением соседних карточек
const ITEM_STAGGER_MS = 80
// Интервал автопереключения 3D-витрины в миллисекундах.
const FEATURED_ROTATION_INTERVAL_MS = 3000
const FEATURED_DESIGNS = CATALOG_DESIGNS.filter(item => item.category === 'pvh' && !item.customizable)
const STARS = [1, 2, 3, 4, 5]
const COMMENT_MAX_LENGTH = 300

const StarIcon = () => (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
        <path d="M12 2.5l2.94 5.96 6.56.95-4.75 4.63 1.12 6.54L12 17.5l-5.87 3.08 1.12-6.54L2.5 9.41l6.56-.95z" />
    </svg>
)

interface CatalogRatingProps {
    design: CatalogDesign;
    color?: CatalogColor;
    colorIndex?: number;
    size?: 'compact' | 'large';
}

const CatalogRating = ({design, color, colorIndex, size = 'compact'}: CatalogRatingProps) => {
    const [hover, setHover] = useState<number | null>(null)
    const [value, setValue] = useState<number | null>(null)
    const [comment, setComment] = useState('')
    const [sending, setSending] = useState(false)
    const [message, setMessage] = useState<string | null>(null)

    const sendRating = async (rating: number) => {
        if (sending) return
        setSending(true)
        setMessage(null)
        try {
            const res = await fetch(`${API_BASE_URL}/post-catalog-raiting`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    design_id: design.id,
                    design_title: design.title,
                    color_id: color ? `${color.id}-${colorIndex ?? 0}` : null,
                    color_label: color?.label ?? null,
                    raiting: rating,
                    comment: comment.trim() || null,
                }),
            })
            if (!res.ok) throw new Error('request failed')
            setValue(rating)
            setMessage('Спасибо за оценку!')
        } catch {
            setMessage('Не удалось отправить оценку')
        } finally {
            setSending(false)
        }
    }

    const shown = hover ?? value ?? 0

    return (
        <div
            className={`catalog-rating catalog-rating-${size}`}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <div className="catalog-rating-stars" onMouseLeave={() => setHover(null)}>
                {STARS.map((star) => (
                    <button
                        key={star}
                        type="button"
                        className={`catalog-rating-star${shown >= star ? ' is-filled' : ''}`}
                        aria-label={`Оценить на ${star}`}
                        disabled={sending}
                        onMouseEnter={() => setHover(star)}
                        onClick={() => sendRating(star)}
                    >
                        <StarIcon />
                    </button>
                ))}
            </div>
            {/* <textarea
                className="catalog-rating-comment jost"
                aria-label="Комментарий к оценке"
                placeholder="Комментарий"
                maxLength={COMMENT_MAX_LENGTH}
                value={comment}
                disabled={sending}
                onChange={(e) => setComment(e.target.value)}
            /> */}
            {message && <Text size="xs" color={message.startsWith('Спасибо') ? 'lightGray' : 'accent'}>{message}</Text>}
        </div>
    )
}

interface CatalogItemProps {
    design: CatalogDesign;
    index: number;
    onOrder: (color?: CatalogColor) => void;
    onOpen: (colorIndex: number) => void;
    palette: DesignPalette;
}

const CatalogImage = ({src, alt, className}: {src: string; alt: string; className?: string}) => {
    const [loadedSrc, setLoadedSrc] = useState<string | null>(null)
    const isLoaded = loadedSrc === src

    return (
        <div className={`catalog-image-wrap${isLoaded ? ' is-loaded' : ''}${className ? ` ${className}` : ''}`}>
            <div className="catalog-image-skeleton" aria-hidden="true" />
            <img src={src} alt={alt} onLoad={() => setLoadedSrc(src)} />
        </div>
    )
}

const CatalogItem = ({design, index, onOrder, onOpen, palette}: CatalogItemProps) => {
    const [colorIndex, setColorIndex] = useState(0)
    const activeColor = design.colors?.[colorIndex]
    const activeImage = activeColor?.image ?? design.image

    const openCard = () => onOpen(colorIndex)

    return (
        <article
            className={`catalog-item${SPECIAL_POSITION ? ' catalog-item-special-position' : ''}`}
            style={{animationDelay: `${index * ITEM_STAGGER_MS}ms`}}
            role="button"
            tabIndex={0}
            onClick={openCard}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openCard()
                }
            }}
            aria-label={`Открыть дизайн ${design.title}`}
        >
            <div className="catalog-item-preview">
                {design.customizable ? <CustomDesignPreview palette={palette} title={design.title} /> : <CatalogImage src={activeImage} alt={design.title} className={design.modelUrl ? 'catalog-model-image' : undefined} />}
                {design.badge && <span className="catalog-item-badge">{design.badge}</span>}
                {NO_NFC_CATEGORIES.includes(design.category) && <span className="catalog-item-no-nfc">Без NFC</span>}
            </div>

            <div className="catalog-item-body">
                <div className="catalog-item-info">
                    <span className="catalog-kicker">{design.modelUrl ? "НАСТОЛЬНАЯ КАРТОЧКА" : design.category === "paper" ? "БУМАГА / QR" : design.category === "pvh" ? "ПЛАСТИК / QR + NFC" : "ВАШ ДИЗАЙН"}</span>
                    <Text size="l" color="white">{design.title}</Text>
                    <Text size="s" color="lightGray">{design.description}</Text>
                </div>

                {design.colors && (
                    <div
                        className="catalog-item-colors"
                        role="radiogroup"
                        aria-label="Цвет карты"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        {design.colors.map((color, i) => (
                            <button
                                type="button"
                                role="radio"
                                aria-checked={i === colorIndex}
                                aria-label={color.label}
                                title={color.label}
                                className={`catalog-item-color${i === colorIndex ? ' is-active' : ''}`}
                                style={{background: color.swatch}}
                                key={`${color.id}-${i}`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setColorIndex(i)
                                }}
                            />
                        ))}
                        <Text size="xs" color="lightGray">{activeColor?.label ?? ''}</Text>
                    </div>
                )}

                <div className="catalog-item-footer" onClick={(e) => e.stopPropagation()}>
                    <span className="catalog-item-price">{formatPrice(design.price)}</span>
                    {SHOW_CATALOG_RATING ? (
                        <CatalogRating design={design} color={activeColor} colorIndex={colorIndex} />
                    ) : (
                        <Button
                            variant="solid"
                            textSize="s"
                            onClick={() => {
                                if (design.customizable) openCard()
                                else onOrder(activeColor)
                            }}
                        >
                            {design.customizable ? 'Выбрать цвета' : 'Заказать'}
                        </Button>
                    )}
                </div>
            </div>
        </article>
    )
}

interface CatalogPreviewState {
    design: CatalogDesign;
    colorIndex: number;
}

interface CatalogPreviewModalProps {
    preview: CatalogPreviewState;
    onClose: () => void;
    onOrder: (design: CatalogDesign, color?: CatalogColor, palette?: DesignPalette) => void;
    palette: DesignPalette;
    onPaletteChange: (palette: DesignPalette) => void;
}

const CatalogPreviewModal = ({preview, onClose, onOrder, palette, onPaletteChange}: CatalogPreviewModalProps) => {
    const [colorIndex, setColorIndex] = useState(preview.colorIndex)
    const [show3D, setShow3D] = useState(true)
    const {design} = preview
    const activeColor = design.colors?.[colorIndex]
    const activeImage = activeColor?.image ?? design.image

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [onClose])

    return (
        <div className="catalog-preview-overlay" onClick={onClose}>
            <div className="catalog-preview-modal" role="dialog" aria-modal="true" aria-label={design.title} onClick={(e) => e.stopPropagation()}>
                <button type="button" className="catalog-preview-close" aria-label="Закрыть" onClick={onClose}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                        <path d="M2 2l10 10M12 2L2 12" />
                    </svg>
                </button>

                <div className="catalog-preview-media">
                    {design.customizable ? <CustomDesignPreview palette={palette} title={design.title} large /> : <>{show3D ? <Catalog3D design={design} colorIndex={colorIndex} /> : <CatalogImage src={activeImage} alt={design.title} className={`catalog-preview-image${design.modelUrl ? ' catalog-model-image' : ''}`} />}<button type="button" className="catalog-view-toggle" onClick={() => setShow3D(!show3D)} aria-pressed={show3D}>{show3D ? "Смотреть изображение" : "Смотреть в 3D"}</button></>}
                    {design.badge && <span className="catalog-item-badge">{design.badge}</span>}
                    {NO_NFC_CATEGORIES.includes(design.category) && <span className="catalog-item-no-nfc">Без NFC</span>}
                </div>

                <div className="catalog-preview-body">
                    <div className="catalog-preview-copy">
                        <span className="catalog-kicker">{design.modelUrl ? "НАСТОЛЬНАЯ КАРТОЧКА" : design.category === "paper" ? "БУМАГА · QR" : design.category === "pvh" ? "ПЛАСТИК · QR + NFC" : "ИНДИВИДУАЛЬНЫЙ ДИЗАЙН"}</span>
                        <Text size="xl" color="white">{design.title}</Text>
                        <Text size="m" color="lightGray">{design.description}</Text>
                    </div>

                    {design.colors && (
                        <div className="catalog-item-colors catalog-preview-colors" role="radiogroup" aria-label="Цвет карты">
                            {design.colors.map((color, i) => (
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={i === colorIndex}
                                    aria-label={color.label}
                                    title={color.label}
                                    className={`catalog-item-color${i === colorIndex ? ' is-active' : ''}`}
                                    style={{background: color.swatch}}
                                    key={`${color.id}-${i}`}
                                    onClick={() => setColorIndex(i)}
                                />
                            ))}
                            <Text size="s" color="lightGray">{activeColor?.label ?? ''}</Text>
                        </div>
                    )}

                    {design.customizable && <DesignColorEditor palette={palette} onChange={onPaletteChange} />}

                    <div className={`catalog-preview-footer${SHOW_CATALOG_RATING ? ' catalog-preview-footer-rating' : ''}`}>
                        <span className="catalog-item-price">{formatPrice(design.price)}</span>
                        {SHOW_CATALOG_RATING ? (
                            <CatalogRating design={design} color={activeColor} colorIndex={colorIndex} size="large" />
                        ) : (
                            <Button variant="solid" textSize="s" onClick={() => onOrder(design, activeColor, design.customizable ? {...palette} : undefined)}>Заказать</Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export const CatalogPage = () => {
    const [customPalette, setCustomPalette] = useState<DesignPalette>({...DEFAULT_DESIGN_PALETTE})
    const [category, setCategory] = useState<CatalogCategory | 'all'>('all')
    const [orderedDesign, setOrderedDesign] = useState<{design: CatalogDesign; color?: CatalogColor; palette?: DesignPalette} | null>(null)
    const [preview, setPreview] = useState<CatalogPreviewState | null>(null)

    const [heroPanel, setHeroPanel] = useState<'savings' | 'model'>('savings')
    const [featuredIndex, setFeaturedIndex] = useState(0)
    const featured = FEATURED_DESIGNS
    const featuredRef = useRef<HTMLDivElement>(null)
    const [featuredVisible, setFeaturedVisible] = useState(false)
    const [featuredInteracting, setFeaturedInteracting] = useState(false)
    const [rotationEnabled, setRotationEnabled] = useState(false)
    const [selectionVersion, setSelectionVersion] = useState(0)

    useEffect(() => {
        const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
        const update = () => setRotationEnabled(!document.hidden && !preference.matches)
        update()
        document.addEventListener('visibilitychange', update)
        preference.addEventListener('change', update)
        const observer = new IntersectionObserver(([entry]) => setFeaturedVisible(entry.isIntersecting), {threshold: 0.2})
        if (featuredRef.current) observer.observe(featuredRef.current)
        return () => {
            observer.disconnect()
            document.removeEventListener('visibilitychange', update)
            preference.removeEventListener('change', update)
        }
    }, [])

    useEffect(() => {
        if (heroPanel !== 'model' || !rotationEnabled || !featuredVisible || featuredInteracting || preview || orderedDesign || featured.length < 2) return
        const timer = window.setTimeout(() => setFeaturedIndex(index => (index + 1) % featured.length), FEATURED_ROTATION_INTERVAL_MS)
        return () => window.clearTimeout(timer)
    }, [heroPanel, featuredIndex, selectionVersion, rotationEnabled, featuredVisible, featuredInteracting, preview, orderedDesign, featured.length])
    useEffect(() => {
        if (!rotationEnabled || !featuredVisible || featuredInteracting || preview || orderedDesign || !featured.length) return
        const timer = window.setTimeout(() => setHeroPanel(panel => panel === 'savings' ? 'model' : 'savings'), 15_000)
        return () => window.clearTimeout(timer)
    }, [heroPanel, rotationEnabled, featuredVisible, featuredInteracting, preview, orderedDesign, featured.length])

    const featuredDesign = featured[featuredIndex]
    const collectionRef = useRef<HTMLDivElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)
    // карточки монтируем в конце вступительной анимации, иначе их CSS-анимация обгоняет текст
    const [isIntroDone, setIsIntroDone] = useState(false)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setIsIntroDone(true); return }
        const ctx = gsap.context(() => {
            gsap.timeline({defaults: {ease: 'power3.out'}})
                .from('.catalog-logo', {opacity: 0, y: -12, duration: 0.6})
                .from('.catalog-eyebrow', {opacity: 0, y: 12, duration: 0.5}, '-=0.3')
                .from('.catalog-title', {opacity: 0, y: 16, duration: 0.6}, '-=0.3')
                .from('.catalog-subtitle', {opacity: 0, y: 12, duration: 0.5}, '-=0.35')
                .from('.catalog-filters', {opacity: 0, y: 12, duration: 0.5}, '-=0.3')
                .call(() => setIsIntroDone(true), undefined, '-=0.2')
        }, rootRef)

        return () => ctx.revert()
    }, [])

    const activeCategory = CATALOG_CATEGORIES.find((item) => item.id === category)
    const designs = category === 'all' ? CATALOG_DESIGNS : CATALOG_DESIGNS.filter((design) => design.category === category)

    return (
        <PageComponent center={false}>
            <div className="catalog" ref={rootRef}>
                <div className="catalog-content">
                    <nav className="catalog-nav" aria-label="Навигация каталога">
                        <a href="/" className="catalog-logo" aria-label="На главную">
                            <img src={vIcon} alt="" /><img src={vIcon} style={{transform: 'rotate(180deg)'}} alt="" />
                        </a>
                        {/* <span>КОЛЛЕКЦИЯ VLINK</span><a href="/">О платформе ↗</a> */}
                    </nav>
                    <section className="catalog-hero">
                        <header className="catalog-header">
                            <div className="catalog-eyebrow catalog-kicker">НАЙДЕТСЯ КАРТА НА ЛЮБОЙ ВКУС И ЦВЕТ.</div>
                            <h1 className="catalog-title">Впечатляйте<br />клиентов<br /><em>с помощью карты.</em></h1>
                            <p className="catalog-subtitle">Сдержанная или яркая, для себя или вашего дела. Выберите дизайн или предложите свой.</p>
                            <Button variant="solid" textSize="s" onClick={() => collectionRef.current?.scrollIntoView({behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'})}>Смотреть коллекцию ↓</Button>
                            <div className="catalog-hero-facts"><span>QR на каждой карте</span><span>NFC в пластике</span><span>Ссылки можно менять</span></div>
                        </header>
                        <div className="catalog-hero-showcase" ref={featuredRef} onPointerDown={() => setFeaturedInteracting(true)} onPointerUp={() => setFeaturedInteracting(false)} onPointerCancel={() => setFeaturedInteracting(false)} onPointerLeave={() => setFeaturedInteracting(false)}>
                            <div className="catalog-hero-panels">
                                <div className={`catalog-hero-panel${heroPanel === 'savings' ? ' is-active' : ''}`} aria-hidden={heroPanel !== 'savings'} inert={heroPanel !== 'savings'}>
                                    <CatalogSavings paused={heroPanel !== 'savings' || !!preview || !!orderedDesign} />
                                </div>
                                <div className={`catalog-hero-panel${heroPanel === 'model' ? ' is-active' : ''}`} aria-hidden={heroPanel !== 'model'} inert={heroPanel !== 'model'}>
                        {featuredDesign && <div className="catalog-featured">
                            <span className="catalog-featured-label">РАССМОТРИТЕ СО ВСЕХ СТОРОН <span>3D</span></span>
                            <Catalog3D design={featuredDesign} designs={featured} designIndex={featuredIndex} paused={heroPanel !== 'model' || !!preview || !!orderedDesign} />
                            <div className="catalog-featured-bottom"><div><span className="catalog-kicker">В ФОКУСЕ</span><h2>{featuredDesign.title}</h2><p>{formatPrice(featuredDesign.price)}</p></div><button className="catalog-featured-open" type="button" aria-label={`Подробнее о дизайне ${featuredDesign.title}`} onClick={() => setPreview({design: featuredDesign, colorIndex: 0})}>↗</button></div>
                            <div className="catalog-featured-options" role="group" aria-label="Дизайн в 3D-витрине">{featured.map((design, i) => <button type="button" key={design.id} aria-label={design.title} aria-pressed={i === featuredIndex} onClick={() => { setFeaturedIndex(i); setSelectionVersion(version => version + 1) }}><img src={design.image} alt="" /></button>)}</div>
                        </div>}
                                </div>
                            </div>
                            <div className="catalog-hero-switch" role="group" aria-label="Витрина">
                                <button type="button" aria-pressed={heroPanel === 'savings'} onClick={() => setHeroPanel('savings')}>Калькулятор выгоды</button>
                                <button type="button" aria-pressed={heroPanel === 'model'} onClick={() => setHeroPanel('model')}>Каталог</button>
                            </div>
                        </div>
                    </section>
                    <div className="catalog-collection-heading" ref={collectionRef}><div><span className="catalog-kicker">НАЙДИТЕ СВОЮ</span><h2>Коллекция карт</h2></div><span aria-live="polite">{designs.length} в коллекции</span></div>
                    <div className="catalog-filters" role="tablist" aria-label="Категории">
                        {CATALOG_CATEGORIES.map((item) => (
                            <button
                                type="button"
                                role="tab"
                                aria-selected={item.id === category}
                                className={`catalog-filter${item.id === category ? ' is-active' : ''}`}
                                key={item.id}
                                onClick={() => setCategory(item.id)}
                            >
                                {item.label}<span className="catalog-filter-count">{item.id === 'all' ? CATALOG_DESIGNS.length : CATALOG_DESIGNS.filter(design => design.category === item.id).length}</span>
                            </button>
                        ))}
                    </div>

                    {activeCategory?.note && (
                        <div className="catalog-note">
                            <Text size="s" color="lightGray">{activeCategory.note}</Text>
                        </div>
                    )}

                    {!isIntroDone ? null : designs.length > 0 ? (
                        <div className="catalog-grid">
                            {designs.map((design, i) => (
                                <CatalogItem
                                    design={design}
                                    palette={customPalette}
                                    index={i}
                                    key={design.id}
                                    onOrder={(color) => setOrderedDesign({design, color})}
                                    onOpen={(colorIndex) => setPreview({design, colorIndex})}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="catalog-empty">
                            <Text size="s" color="lightGray">В этой категории пока нет дизайнов</Text>
                        </div>
                    )}
                    <footer className="catalog-bottom-note"><span>01 / ВЫБЕРИТЕ ДИЗАЙН</span><span>02 / ОБСУДИМ ЗАКАЗ</span><span>03 / ПОЛУЧИТЕ КАРТУ</span><p>Карта остаётся с вами. Ссылки меняются вместе с вашим делом.</p></footer>
                </div>
            </div>

            {preview && (
                <CatalogPreviewModal
                    preview={preview}
                    palette={customPalette}
                    onPaletteChange={setCustomPalette}
                    onClose={() => setPreview(null)}
                    onOrder={(design, color, palette) => {
                        setPreview(null)
                        setOrderedDesign({design, color, palette})
                    }}
                />
            )}
            {orderedDesign && <SupportModal showText={false} message={getOrderMessage(orderedDesign.design, orderedDesign.color, orderedDesign.palette)} onClose={() => setOrderedDesign(null)} />}
        </PageComponent>
    )
}
